import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Fix: Get token from localStorage and ensure it's a valid string
  const getStoredToken = () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken || storedToken === 'undefined' || storedToken === 'null' || storedToken === '[object Object]') {
      return null;
    }
    // Check if it looks like a JWT (has 3 parts)
    const parts = storedToken.split('.');
    if (parts.length !== 3) {
      console.error('Invalid token format in localStorage, clearing it');
      localStorage.removeItem('token');
      return null;
    }
    return storedToken;
  };

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Set auth token
  const setAuthToken = (tokenValue) => {
    if (tokenValue && tokenValue !== 'undefined' && tokenValue !== 'null' && tokenValue !== '[object Object]') {
      // Clean the token if it has quotes
      let cleanToken = tokenValue;
      if (cleanToken.startsWith('"') && cleanToken.endsWith('"')) {
        cleanToken = cleanToken.slice(1, -1);
      }
      if (cleanToken.startsWith("'") && cleanToken.endsWith("'")) {
        cleanToken = cleanToken.slice(1, -1);
      }
      
      api.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      localStorage.setItem('token', cleanToken);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const needsProfileCompletion = user && 
    (!user.age || !user.sex || !user.user_phone);

  // Load user
  const loadUser = async () => {
    try {
      const currentToken = getStoredToken();
      if (!currentToken) {
        setLoading(false);
        return;
      }
      
      setAuthToken(currentToken);
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Load user error:', err);
      // If token is invalid, clear it
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Login user - FIXED: Accept object with token and user properties
  const login = async(tokenValue, userData)  => {
    // Handle both parameter styles
    let finalToken = tokenValue;
    let finalUser = userData;
    
    // If tokenValue is an object that contains token (like response.data)
    if (tokenValue && typeof tokenValue === 'object' && tokenValue.token) {
      finalToken = tokenValue.token;
      finalUser = tokenValue.user || userData;
    }
    
    // If tokenValue is a string but contains the user object as string
    if (typeof finalToken === 'string' && finalToken.startsWith('{')) {
      try {
        const parsed = JSON.parse(finalToken);
        if (parsed.token) {
          finalToken = parsed.token;
          finalUser = parsed.user || finalUser;
        }
      } catch (e) {
        // Not JSON, continue
      }
    }
    
    // Validate token format
    if (!finalToken || finalToken === 'undefined' || finalToken === 'null' || finalToken === '[object Object]') {
      console.error('Invalid token provided to login:', finalToken);
      return;
    }
    
    const parts = finalToken.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format in login, expected 3 parts, got', parts.length);
      return;
    }
    
    console.log('Login - Setting valid token');
    setToken(finalToken);
    setAuthToken(finalToken);
    setUser(finalUser);
    setIsAuthenticated(true);
    localStorage.removeItem('tableId');
    await loadUser();
    navigate('/');
  };

  // Logout user
  const logout = () => {
    setToken(null);
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Check if token exists on app load
  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        login,
        logout,
        loadUser,
        updateUser,
        needsProfileCompletion
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};